import {
  Children,
  cloneElement,
  Fragment,
  isValidElement,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { Menu, MenuSeparator } from "../Menu/Menu";

// Gap between the visible buttons (and to the kebab). gap-3 to match the
// ControlBar's standard spacing so the header reads uniform.
const GAP_PX = 12;
// Width reserved for the kebab trigger (+ the gap to it) once anything overflows.
const KEBAB_PX = 44;
// Slack kept between the (non-shrinking) visible group and the available width,
// so a small measurement difference folds a button instead of overflowing.
const SAFETY_PX = 12;

// Flatten children into one element per button, recursing through Fragments so
// `actions={<>…</>}` (the common shape) doesn't collapse to a single item.
function flattenItems(children: ReactNode): ReactElement[] {
  const out: ReactElement[] = [];
  const walk = (nodes: ReactNode, prefix: string) => {
    Children.forEach(nodes, (child, i) => {
      if (!isValidElement(child)) return;
      const path = `${prefix}${i}`;
      if (child.type === Fragment) {
        walk((child.props as { children?: ReactNode }).children, `${path}.`);
      } else {
        out.push(child.key ? child : cloneElement(child, { key: path }));
      }
    });
  };
  walk(children, "ab");
  return out;
}

interface ActionBarProps {
  /**
   * A pure run of action buttons (`<Button icon>label</Button>`). The buttons
   * that fit render normally, with their full labels. The ones that don't fit
   * fold into the kebab, last one first. Mixed controls (badges, switches, solo
   * inputs) do not belong here — use a ControlBar for those.
   */
  children: ReactNode;
  /**
   * Existing kebab items (Button children). They always live in the menu;
   * overflowed actions are prepended above them, separated by a divider.
   */
  menu?: ReactNode;
  /**
   * Non-collapsing content pinned to the LEFT of the action group (page-header
   * stats). It renders inside the measured row so the whole cluster packs to
   * the right edge, and its measured width is subtracted from the space the
   * actions get — so folding into the kebab accounts for the room the stats take.
   */
  leading?: ReactNode;
}

/**
 * A stable row of action buttons: the ones that fit show with their full label,
 * the rest fold into an overflow kebab (last first). No hover tricks, no
 * icon-collapse — what you see is a plain toolbar that overflows gracefully.
 *
 * The number of visible buttons is a PURE function of (available width, each
 * button's natural width). Available width is measured on a block wrapper that
 * is always 100% of its parent, so it never depends on how many buttons are
 * currently visible — no feedback loop, no flicker. Each button's natural width
 * is measured once from a hidden copy of the row.
 */
export function ActionBar({ children, menu, leading }: ActionBarProps) {
  const items = flattenItems(children);
  const count = items.length;

  // Available width — measured on a block wrapper (width 100%), stable read.
  // Starts at Infinity so everything shows until measured.
  const rootRef = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState(Number.POSITIVE_INFINITY);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const read = () => {
      const w = el.clientWidth;
      if (w > 0) setAvail(w);
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Width of the non-collapsing leading block (stats). The actions are sized
  // against the space LEFT OVER after it, so the fold accounts for the room the
  // stats take. Measured with a ResizeObserver (stats values change at runtime).
  const leadingRef = useRef<HTMLDivElement>(null);
  const [leadingWidth, setLeadingWidth] = useState(0);
  useLayoutEffect(() => {
    const el = leadingRef.current;
    if (!el) {
      setLeadingWidth(0);
      return;
    }
    const read = () => setLeadingWidth(el.offsetWidth);
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [leading]);

  // Natural (icon + label) width of each button, measured once from a hidden
  // copy of the row. This is exactly the width a visible button occupies, since
  // visible buttons render the same way (full label).
  const measureRef = useRef<HTMLDivElement>(null);
  const [itemWidths, setItemWidths] = useState<number[]>([]);
  useLayoutEffect(() => {
    const row = measureRef.current;
    if (!row) return;
    const widths = Array.from(row.children).map(
      (c) => (c as HTMLElement).offsetWidth,
    );
    setItemWidths((prev) =>
      prev.length === widths.length && prev.every((w, i) => w === widths[i])
        ? prev
        : widths,
    );
  }, [count]);

  // Some actions render nothing (a Menu with no items, a permission-gated
  // button) — they measure 0px. They must NOT block the fold: drop them so the
  // group is sized only by the buttons that actually render. (The hidden
  // measuring row below still renders every item to measure it.)
  //
  // `hasLayout` guards the jsdom / pre-paint case where EVERY item measures 0:
  // that's "no layout yet", not "all empty", so we keep them all and show
  // everything until real widths arrive.
  const hasLayout =
    itemWidths.length === count && itemWidths.some((w) => w > 0);
  const realItems = hasLayout
    ? items.filter((_, i) => itemWidths[i]! > 0)
    : items;
  const realCount = realItems.length;

  // How many real buttons fit — drop from the end one at a time. The width for
  // `v` buttons is simply their natural widths plus the gaps between them (they
  // show full, no collapse). Returns Infinity until laid out so everything shows.
  const visibleCount = useMemo(() => {
    const laidOut =
      itemWidths.length === count && itemWidths.some((w) => w > 0);
    if (!laidOut || !Number.isFinite(avail)) {
      return Number.POSITIVE_INFINITY;
    }
    // The actions share the row with the (non-collapsing) leading stats, so
    // their budget is whatever width is left after the stats and the gap to them.
    const availForActions =
      avail - (leadingWidth > 0 ? leadingWidth + GAP_PX : 0);
    const widths = itemWidths.filter((w) => w > 0); // skip empty (0px) items
    const n = widths.length;
    for (let v = n; v >= 1; v--) {
      const groupWidth =
        widths.slice(0, v).reduce((s, w) => s + w, 0) + (v - 1) * GAP_PX;
      const needsKebab = v < n || !!menu;
      const need = groupWidth + (needsKebab ? KEBAB_PX + GAP_PX : 0);
      // SAFETY_PX of slack so the (non-shrinking) visible group always fits with
      // a little room — folds one button early rather than letting it overflow.
      if (need + SAFETY_PX <= availForActions) return v;
    }
    return 0;
  }, [avail, itemWidths, count, menu, leadingWidth]);

  const visible = realItems.slice(0, visibleCount);
  const overflow = realItems.slice(visibleCount);
  const hasMenu = overflow.length > 0 || !!menu;

  if (realCount === 0 && !menu && !leading) return undefined;

  const rowStyle: CSSProperties = {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: `${GAP_PX}px`,
  };

  // Zero-footprint clip wrapper for the measuring row. The row lays out at the
  // buttons' full natural width to be measured, but an absolute, full-width row
  // at left:0 pokes past a narrow viewport's right edge and makes the WHOLE PAGE
  // scroll horizontally. A 0×0 `overflow:hidden` wrapper contains it: children
  // still report their natural offsetWidth, but nothing extends the page.
  const hiddenWrapperStyle: CSSProperties = {
    position: "absolute",
    top: -9999,
    left: 0,
    width: 0,
    height: 0,
    overflow: "hidden",
    pointerEvents: "none",
  };
  const hiddenRowStyle: CSSProperties = {
    display: "flex",
    flexWrap: "nowrap",
    gap: `${GAP_PX}px`,
  };

  return (
    // min-width:0 is essential: as a flex item the root's default min-width is
    // its content (min-content), so without it the root never shrinks below the
    // buttons' width — clientWidth then reports the content width, not the space
    // actually available, and nothing ever folds. With it, the root shrinks to
    // its allotment, we measure the real available width, and overflow works.
    <div
      ref={rootRef}
      style={{ width: "100%", minWidth: 0, position: "relative" }}
    >
      <div style={rowStyle}>
        {leading && (
          // Non-collapsing stats, pinned left of the action group. flexShrink:0
          // so it keeps its width; the actions fold into the kebab instead.
          <div ref={leadingRef} style={{ flexShrink: 0 }}>
            {leading}
          </div>
        )}
        {visible.map((child) => (
          // flexShrink:0 so a visible button keeps its full width instead of the
          // browser squishing it — when space is tight we fold a button into the
          // kebab (above) rather than shrink the row.
          <div key={child.key} style={{ flexShrink: 0 }}>
            {child}
          </div>
        ))}
        {hasMenu && (
          <div style={{ flex: "0 0 auto" }}>
            <Menu>
              {overflow}
              {overflow.length > 0 && menu && <MenuSeparator />}
              {menu}
            </Menu>
          </div>
        )}
      </div>

      {/* Hidden measuring copy of the full row — each button at natural width.
          Wrapped in a 0×0 clip so it can't extend the page's scroll width.
          Strip identity attributes (`data-testid`, `id`) from these clones: the
          row exists only to measure widths, but the copies are real DOM nodes,
          so leaving them in would put two elements with the same `data-testid`
          (and the same `id`) on the page. Tests selecting a header action by
          testid would then match 2 elements, and `cy.click()` throws. */}
      {/* inert (not just aria-hidden/pointer-events) so the cloned, fully-wired
          action buttons are also removed from the focus tree — otherwise Tab +
          Enter could trigger a clone's onClick/dialog. */}
      <div aria-hidden inert style={hiddenWrapperStyle}>
        <div ref={measureRef} style={hiddenRowStyle}>
          {items.map((child) => (
            <div key={child.key} style={{ flex: "0 0 auto" }}>
              {cloneElement(child as ReactElement<Record<string, unknown>>, {
                "data-testid": undefined,
                id: undefined,
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
